import {
	PassengerTypeEnum,
	ServiceUnitEnum,
	TransferTypeEnum,
	TypeServiceEnum,
	serviceTypes,
} from 'features/salesOrder/types';
import { calculatMarkupPercentaje } from 'features/salesOrder/components/SalesOrderServiceForm/helpers';

export type SaleOrderServiceRecord = Record<string, unknown> & {
	keyId: string;
	serviceType: TypeServiceEnum;
	enable?: boolean;
	reference?: string;
	currency?: string;
	totalCost?: number | string;
	totalSaleAmount?: number | string;
	cost?: number | string;
	saleAmount?: number | string;
	unit?: ServiceUnitEnum | string;
	passengerIds?: string[];
	tariffProvider?: { id?: string; fantasyName?: string; businessName?: string };
	serviceProvider?: { id?: string; fantasyName?: string; businessName?: string };
	sameTariffServiceProvider?: boolean;
	passengersGroupByPassengerType?: Array<{
		passengerType?: PassengerTypeEnum | string;
		totalCost?: number;
		totalSaleAmount?: number;
		passengerIds?: string[];
		passengersTickets?: Array<{ passengerId: string; ticket: string }>;
	}>;
	passengersTickets?: Array<{ passengerId: string; ticket: string }>;
	rooms?: Array<{
		cost?: number | string;
		saleAmount?: number | string;
		totalCost?: number;
		totalSaleAmount?: number;
	}>;
};

export enum DeactivateScenario {
	NoCost = 'no_cost',
	WithPenalty = 'with_penalty',
	Manual = 'manual',
}

const COMPLEX_SERVICE_TYPES = new Set<TypeServiceEnum>([TypeServiceEnum.Flight, TypeServiceEnum.Accommodation]);

export const isComplexServiceType = (serviceType: TypeServiceEnum): boolean => COMPLEX_SERVICE_TYPES.has(serviceType);

export const isLinearServiceType = (serviceType: TypeServiceEnum): boolean => !isComplexServiceType(serviceType);

export interface ServiceProviderInfo {
	label: string;
	name: string;
	id?: string;
}

export interface ServiceFinancialBreakdownLine {
	label: string;
	cost: number;
	saleAmount: number;
}

export interface ServiceFinancialSummary {
	totalCost: number;
	totalSaleAmount: number;
	currency: string;
	breakdown: ServiceFinancialBreakdownLine[];
}

export interface ServiceProviderSummary {
	tariffProvider: ServiceProviderInfo | null;
	serviceProvider: ServiceProviderInfo | null;
	showBoth: boolean;
}

const toNumber = (value: unknown): number => {
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : 0;
};

const getProviderName = (provider?: { fantasyName?: string; businessName?: string }): string =>
	provider?.fantasyName || provider?.businessName || 'No especificado';

export const getServiceTypeLabel = (serviceType: TypeServiceEnum): string =>
	serviceTypes.find((type) => type.id === serviceType)?.name ?? serviceType;

export const getServiceDescription = (service: SaleOrderServiceRecord): string => {
	if (!service?.serviceType) {
		return 'No especificado';
	}

	switch (service.serviceType) {
		case TypeServiceEnum.Accommodation: {
			const destinationCode = (service as SaleOrderServiceRecord & { destination?: { code?: string } }).destination
				?.code;
			const serviceProviderName = getProviderName(service.serviceProvider);
			const roomsLength = service.rooms?.length ?? 0;
			return `${destinationCode ?? 'N/A'}: ${serviceProviderName} | ${roomsLength} hab`;
		}
		case TypeServiceEnum.Flight: {
			const vcCode = (service as SaleOrderServiceRecord & { vc?: { code?: string } }).vc?.code ?? 'N/A';
			const reference = service.reference ?? 'N/A';
			const passengerCount =
				service.passengersGroupByPassengerType?.reduce((acc, group) => acc + (group.passengerIds?.length ?? 0), 0) ?? 0;
			return `vc: ${vcCode} | pnr: ${reference} | ${passengerCount} pax`;
		}
		case TypeServiceEnum.FlightAncillary: {
			const flightAncillaryType =
				(service as SaleOrderServiceRecord & { flightAncillaryType?: string }).flightAncillaryType ?? 'N/A';
			const vcCode = (service as SaleOrderServiceRecord & { vc?: { code?: string } }).vc?.code ?? 'N/A';
			const reference = service.reference ?? 'N/A';
			const passengerCount = service.passengerIds?.length ?? 0;
			return `${flightAncillaryType} | vc: ${vcCode} | pnr: ${reference} | ${passengerCount} pax`;
		}
		case TypeServiceEnum.Other: {
			const otherServiceType =
				(service as SaleOrderServiceRecord & { otherServiceType?: string }).otherServiceType ?? 'Otro';
			const serviceName = (service as SaleOrderServiceRecord & { serviceName?: string }).serviceName ?? 'N/A';
			const passengerCount = service.passengerIds?.length ?? 0;
			return `${otherServiceType}: ${serviceName} | ${passengerCount} pax`;
		}
		case TypeServiceEnum.Transfer: {
			const transferSegment = (
				service as SaleOrderServiceRecord & {
					transferSegments?: Array<{ transferType?: string; originName?: string; destinationName?: string }>;
				}
			).transferSegments?.[0];
			const transferType = transferSegment?.transferType ?? 'N/A';
			const originName = transferSegment?.originName ?? 'N/A';
			const destinationName = transferSegment?.destinationName ?? 'N/A';
			const passengerCount = service.passengerIds?.length ?? 0;
			return `${transferType} ${originName} → ${destinationName} | ${passengerCount} pax`;
		}
		case TypeServiceEnum.Fee: {
			const feeServiceType = (service as SaleOrderServiceRecord & { feeServiceType?: string }).feeServiceType ?? 'Fee';
			const passengerCount = service.passengerIds?.length ?? 0;
			return `${feeServiceType} | ${passengerCount} pax`;
		}
		default:
			return service.reference ? `Ref: ${service.reference}` : service.serviceType;
	}
};

export const getServiceFinancialSummary = (service: SaleOrderServiceRecord): ServiceFinancialSummary => {
	const currency = service.currency ?? 'USD';
	const breakdown: ServiceFinancialBreakdownLine[] = [];

	if (service.serviceType === TypeServiceEnum.Flight && service.passengersGroupByPassengerType?.length) {
		service.passengersGroupByPassengerType.forEach((group) => {
			breakdown.push({
				label: `Tipo ${group.passengerType ?? 'N/A'} (${group.passengerIds?.length ?? 0} pax)`,
				cost: toNumber(group.totalCost),
				saleAmount: toNumber(group.totalSaleAmount),
			});
		});
	} else if (service.serviceType === TypeServiceEnum.Accommodation && service.rooms?.length) {
		service.rooms.forEach((room, index) => {
			breakdown.push({
				label: `Habitación ${index + 1}`,
				cost: toNumber(room.totalCost ?? room.cost),
				saleAmount: toNumber(room.totalSaleAmount ?? room.saleAmount),
			});
		});
	}

	const totalCost = toNumber(service.totalCost);
	const totalSaleAmount = toNumber(service.totalSaleAmount);

	return {
		totalCost,
		totalSaleAmount,
		currency,
		breakdown,
	};
};

export const getServiceProviderSummary = (service: SaleOrderServiceRecord): ServiceProviderSummary => {
	const tariffProvider = service.tariffProvider
		? {
				label: 'Proveedor tarifa',
				name: getProviderName(service.tariffProvider),
				id: service.tariffProvider.id,
		  }
		: null;

	const serviceProvider = service.serviceProvider
		? {
				label: 'Proveedor servicio',
				name: getProviderName(service.serviceProvider),
				id: service.serviceProvider.id,
		  }
		: null;

	const showBoth = Boolean(
		serviceProvider &&
			tariffProvider &&
			(!service.sameTariffServiceProvider || serviceProvider.id !== tariffProvider.id),
	);

	return { tariffProvider, serviceProvider, showBoth };
};

export const amountsAreZero = (totalCost: number, totalSaleAmount: number): boolean =>
	totalCost === 0 && totalSaleAmount === 0;

export const amountsChanged = (saved: ServiceFinancialSummary, editedCost: number, editedSale: number): boolean =>
	saved.totalCost !== editedCost || saved.totalSaleAmount !== editedSale;

export const isValidServiceReference = (reference?: string): boolean =>
	Boolean(reference?.trim() && reference.trim() !== '-');

export const getServiceReferenceLabel = (serviceType: TypeServiceEnum): string => {
	if (serviceType === TypeServiceEnum.Flight || serviceType === TypeServiceEnum.FlightAncillary) {
		return 'PNR';
	}
	return 'Localizador / referencia';
};

export interface ServiceTicketLine {
	passengerId: string;
	passengerName: string;
	ticket: string;
	isValid: boolean;
}

export const isFlightTicketServiceType = (serviceType: TypeServiceEnum): boolean =>
	serviceType === TypeServiceEnum.Flight || serviceType === TypeServiceEnum.FlightAncillary;

export const isValidServiceTicket = (ticket?: string): boolean => Boolean(ticket?.trim() && ticket.trim() !== '-');

export const getPassengerDisplayName = (
	passengers: Array<{ keyId: string; name?: string; lastName?: string }>,
	passengerId: string,
): string => {
	const passenger = passengers.find((p) => p.keyId === passengerId);
	if (!passenger) {
		return 'Pasajero';
	}
	return `${passenger.name ?? ''} ${passenger.lastName ?? ''}`.trim() || 'Pasajero';
};

export const getServiceTicketLines = (
	service: SaleOrderServiceRecord,
	passengers: Array<{ keyId: string; name?: string; lastName?: string }>,
): ServiceTicketLine[] => {
	if (!isFlightTicketServiceType(service.serviceType)) {
		return [];
	}

	const ticketByPassenger = new Map<string, string>();

	if (service.serviceType === TypeServiceEnum.Flight) {
		service.passengersGroupByPassengerType?.forEach((group) => {
			group.passengersTickets?.forEach((entry) => {
				ticketByPassenger.set(entry.passengerId, entry.ticket ?? '');
			});
			group.passengerIds?.forEach((passengerId) => {
				if (!ticketByPassenger.has(passengerId)) {
					ticketByPassenger.set(passengerId, '');
				}
			});
		});
	} else {
		service.passengersTickets?.forEach((entry) => {
			ticketByPassenger.set(entry.passengerId, entry.ticket ?? '');
		});
		service.passengerIds?.forEach((passengerId) => {
			if (!ticketByPassenger.has(passengerId)) {
				ticketByPassenger.set(passengerId, '');
			}
		});
	}

	return Array.from(ticketByPassenger.entries()).map(([passengerId, ticket]) => {
		const trimmedTicket = ticket.trim();
		return {
			passengerId,
			passengerName: getPassengerDisplayName(passengers, passengerId),
			ticket: trimmedTicket || '-',
			isValid: isValidServiceTicket(ticket),
		};
	});
};

export const areAllServiceTicketsValid = (lines: ServiceTicketLine[]): boolean =>
	lines.length > 0 && lines.every((line) => line.isValid);

const getUnitMultiplier = (service: SaleOrderServiceRecord): number => {
	if (service.unit === ServiceUnitEnum.Person) {
		return Math.max(service.passengerIds?.length ?? 1, 1);
	}
	return 1;
};

type ProviderRef = string | { id?: string } | undefined;

const resolveProviderId = (provider: ProviderRef): string | undefined => {
	if (!provider || provider === 'n/a') {
		return undefined;
	}
	if (typeof provider === 'string') {
		return provider;
	}
	return provider.id;
};

const READ_ONLY_PUT_FIELDS = [
	'created',
	'enable',
	'billingStatusDetail',
	'providerAutomaticPaymentDateLine',
	'issueRequest',
] as const;

const normalizeTariffProviderForPut = (payload: Record<string, unknown>): string | undefined => {
	const existingId = payload.tariffProviderId;
	if (typeof existingId === 'string' && existingId !== 'n/a') {
		delete payload.tariffProvider;
		return existingId;
	}

	const tariffProvider = payload.tariffProvider as ProviderRef;
	const tariffProviderId = resolveProviderId(tariffProvider);

	if (tariffProviderId) {
		payload.tariffProviderId = tariffProviderId;
	} else {
		delete payload.tariffProviderId;
	}

	delete payload.tariffProvider;
	return tariffProviderId;
};

const normalizeServiceProviderForPut = (payload: Record<string, unknown>, tariffProviderId?: string): void => {
	const sameTariffServiceProvider = payload.sameTariffServiceProvider === true;
	const existingId = payload.serviceProviderId;

	if (sameTariffServiceProvider && tariffProviderId) {
		payload.serviceProviderId = tariffProviderId;
		delete payload.serviceProvider;
		return;
	}

	if (typeof existingId === 'string' && existingId !== 'n/a') {
		delete payload.serviceProvider;
		return;
	}

	const serviceProviderId = resolveProviderId(payload.serviceProvider as ProviderRef);
	if (serviceProviderId) {
		payload.serviceProviderId = serviceProviderId;
	} else {
		delete payload.serviceProviderId;
	}

	delete payload.serviceProvider;
};

const normalizeTransferSegmentsForPut = (payload: Record<string, unknown>): void => {
	const segments = payload.transferSegments;
	if (!Array.isArray(segments)) {
		return;
	}

	payload.transferSegments = segments.map((segment) => {
		if (!segment || typeof segment !== 'object') {
			return segment;
		}

		const normalizedSegment = { ...(segment as Record<string, unknown>) };
		const transferType = normalizedSegment.transferType;

		if (transferType !== TransferTypeEnum.InOut && transferType !== TransferTypeEnum.InOutPrivate) {
			delete normalizedSegment.endDate;
		}

		return normalizedSegment;
	});
};

const stripReadOnlyFieldsForPut = (payload: Record<string, unknown>): void => {
	READ_ONLY_PUT_FIELDS.forEach((field) => {
		delete payload[field];
	});
};

const prepareLinearServiceForPut = (payload: Record<string, unknown>, country?: string): void => {
	const tariffProviderId = normalizeTariffProviderForPut(payload);
	normalizeServiceProviderForPut(payload, tariffProviderId);
	normalizeTransferSegmentsForPut(payload);
	stripReadOnlyFieldsForPut(payload);

	if (!Array.isArray(payload.providerManualPaymentDateLines)) {
		payload.providerManualPaymentDateLines = [];
	}

	if (payload.totalSaleAmount === null) {
		payload.totalSaleAmount = 0;
	}

	if (country !== 'CO') {
		delete payload.currencyRate;
		delete payload.totalSaleAmountInLocalCurrency;
		delete payload.saleAmountInLocalCurrency;
	}
};

export const buildLinearServicePayload = (
	service: SaleOrderServiceRecord,
	totalCost: number,
	totalSaleAmount: number,
	country?: string,
): SaleOrderServiceRecord => {
	const payload = structuredClone(service) as SaleOrderServiceRecord & Record<string, unknown>;
	const multiplier = getUnitMultiplier(service);
	const unitCost = multiplier > 0 ? totalCost / multiplier : totalCost;
	const unitSale = multiplier > 0 ? totalSaleAmount / multiplier : totalSaleAmount;

	payload.totalCost = totalCost;
	payload.totalSaleAmount = totalSaleAmount;
	payload.cost = Number(unitCost.toFixed(2));
	payload.saleAmount = Number(unitSale.toFixed(2));

	if (totalSaleAmount === 0) {
		delete payload.markupPercentage;
	} else {
		payload.markupPercentage = calculatMarkupPercentaje(totalCost, totalSaleAmount);
	}

	prepareLinearServiceForPut(payload, country);

	return payload;
};

export const formatServiceAmount = (amount: number, currency: string): string => `${currency} ${amount.toFixed(2)}`;
